import { useMemo, useState } from 'react';
import { useApp } from '../store/AppContext.js';
import { VaultItemModal } from '../components/VaultItemModal.js';
import { CopyButton } from '../components/ui.js';
import type { DecryptedItem } from '../crypto/session.js';

export function Vault({ trash = false }: { trash?: boolean }) {
  const { items, deleteItem, restoreItem, toggleFavourite, markUsed } = useApp();
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<DecryptedItem | null>(null);
  const [filter, setFilter] = useState<'all' | 'favourites'>('all');

  const visible = useMemo(() => {
    let list = items.filter((i) => i.deletedAt === trash);
    if (filter === 'favourites') list = list.filter((i) => i.favourite);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter((i) =>
        [i.title, i.username, i.url, ...(i.tags ?? [])].filter(Boolean).some((f) => f!.toLowerCase().includes(needle)),
      );
    }
    return list.sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
  }, [items, q, filter, trash]);

  return (
    <div>
      <div className="flex between wrap mb">
        <div className="search">
          <span className="ic">🔍</span>
          <input placeholder={trash ? 'Search trash…' : 'Search vault…'} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex gap-sm">
          {!trash && (
            <>
              <button className={`btn secondary sm ${filter === 'all' ? '' : 'ghost'}`} onClick={() => setFilter('all')}>All</button>
              <button className={`btn secondary sm ${filter === 'favourites' ? '' : 'ghost'}`} onClick={() => setFilter('favourites')}>★ Favourites</button>
              <button className="btn" onClick={() => setAdding(true)}>＋ Add item</button>
            </>
          )}
        </div>
      </div>

      {visible.length === 0 && (
        <div className="panel center" style={{ padding: 40 }}>
          <div style={{ fontSize: 40 }}>{trash ? '🗑️' : '🔒'}</div>
          <p className="muted">{trash ? 'Trash is empty.' : q ? 'No matches.' : 'Your vault is empty. Add your first item.'}</p>
          {!trash && !q && <button className="btn mt" onClick={() => setAdding(true)}>＋ Add item</button>}
        </div>
      )}

      {visible.map((item) => (
        <ItemRow
          key={item.id}
          item={item}
          trash={trash}
          onEdit={() => setEditing(item)}
          onDelete={() => deleteItem(item.id)}
          onRestore={() => restoreItem(item.id)}
          onPurge={() => deleteItem(item.id, true)}
          onFav={() => toggleFavourite(item.id, !item.favourite)}
          onCopy={() => markUsed(item.id)}
        />
      ))}

      {(adding || editing) && <VaultItemModal onClose={() => { setAdding(false); setEditing(null); }} editing={editing} />}
    </div>
  );
}

function faviconFor(url?: string): string {
  if (!url) return '🔑';
  try {
    const host = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
    return host[0]!.toUpperCase();
  } catch {
    return '🔑';
  }
}

function ItemRow({ item, trash, onEdit, onDelete, onRestore, onPurge, onFav, onCopy }: {
  item: DecryptedItem;
  trash: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onPurge: () => void;
  onFav: () => void;
  onCopy: () => void;
}) {
  return (
    <div className="item-row">
      <div className="icn">{faviconFor(item.url)}</div>
      <div className="meta">
        <div className="title">
          {item.title} {item.favourite && <span style={{ color: 'var(--warn)' }}>★</span>}
        </div>
        <div className="sub">{item.username ?? item.url ?? '—'}</div>
        <div className="flex gap-sm mt-sm">
          {(item.tags ?? []).slice(0, 4).map((t) => <span className="tag" key={t}>{t}</span>)}
        </div>
      </div>
      <div className="actions">
        {!trash && item.password && <CopyButton value={item.password} label="Copy" onAfterCopy={onCopy} />}
        {!trash && <button className="ghost btn sm" title="Favourite" onClick={onFav}>{item.favourite ? '★' : '☆'}</button>}
        {!trash && <button className="ghost btn sm" title="Edit" onClick={onEdit}>✏️</button>}
        {!trash && <button className="ghost btn sm" title="Move to trash" onClick={onDelete}>🗑️</button>}
        {trash && <button className="btn secondary sm" onClick={onRestore}>Restore</button>}
        {trash && <button className="btn danger sm" onClick={() => confirm('Permanently delete?') && onPurge()}>Delete</button>}
      </div>
    </div>
  );
}
